import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { parseCSVFile } from '../lib/parseCSVFile'
import { parseBinanceCSV, parseBybitCSV, parseForexCSV, mergeBinanceFills } from '../lib/csvParsers'
import { syncBinance, syncBybit } from '../lib/exchangeSync'

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'
    }`}
  >
    {children}
  </button>
)

export default function Import() {
  const { user } = useAuth()
  const [tab, setTab] = useState('csv')
  const [broker, setBroker] = useState('binance')
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState([])
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(null)
  const [apiKey, setApiKey] = useState('')
  const [apiSecret, setApiSecret] = useState('')

  const parseRows = (rows, brokerName) => {
    if (brokerName === 'binance') return mergeBinanceFills(parseBinanceCSV(rows))
    if (brokerName === 'bybit') return parseBybitCSV(rows)
    return parseForexCSV(rows)
  }

  const handleFileChange = async (e) => {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setStatus(null)
    const rows = await parseCSVFile(f)
    const parsed = parseRows(rows, broker)
    setPreview(parsed.slice(0, 5))
  }

  const handleCSVImport = async () => {
    if (!file) return
    setLoading(true)
    setStatus(null)
    try {
      const rows = await parseCSVFile(file)
      const parsed = parseRows(rows, broker)
      const trades = parsed.map(t => ({ ...t, user_id: user.id }))
      const { error } = await supabase.from('trades').insert(trades)
      if (error) throw error
      setStatus({ type: 'success', message: `✅ Successfully imported ${trades.length} trades.` })
      setFile(null)
      setPreview([])
    } catch (err) {
      setStatus({ type: 'error', message: `❌ ${err.message}` })
    }
    setLoading(false)
  }

  const handleAPISync = async () => {
    if (!apiKey || !apiSecret) return
    setLoading(true)
    setStatus(null)
    try {
      const trades = broker === 'binance'
        ? await syncBinance(apiKey, apiSecret)
        : await syncBybit(apiKey, apiSecret)
      const payload = trades.map(t => ({ ...t, user_id: user.id }))
      const { error } = await supabase.from('trades').insert(payload)
      if (error) throw error
      setStatus({ type: 'success', message: `✅ Synced ${payload.length} trades from ${broker === 'binance' ? 'Binance' : 'Bybit'}.` })
      setApiKey('')
      setApiSecret('')
    } catch (err) {
      setStatus({ type: 'error', message: `❌ ${err.message}` })
    }
    setLoading(false)
  }

  const inputClass = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500"
  const brokerLabel = broker === 'forex' ? 'FX Broker' : broker.charAt(0).toUpperCase() + broker.slice(1)

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">Import Trades</h1>
        <p className="text-gray-400 text-sm mt-1">Import via CSV file or sync directly from exchange APIs</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <TabButton active={tab === 'csv'} onClick={() => setTab('csv')}>📁 CSV Import</TabButton>
        <TabButton active={tab === 'api'} onClick={() => setTab('api')}>🔗 API Sync</TabButton>
      </div>

      {/* Broker selector */}
      <div className="flex gap-2 mb-6">
        {['binance', 'bybit', 'forex'].map(b => (
          <button
            key={b}
            onClick={() => { setBroker(b); setPreview([]); setFile(null); setStatus(null) }}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors border ${
              broker === b ? 'border-blue-500 bg-blue-600 text-white' : 'border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            {b === 'forex' ? 'FX Broker' : b.charAt(0).toUpperCase() + b.slice(1)}
          </button>
        ))}
      </div>

      {/* CSV Tab */}
      {tab === 'csv' && (
        <div className="space-y-5">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
            <h2 className="text-white font-medium mb-1">Upload CSV</h2>
            <p className="text-gray-500 text-xs mb-4">
              {broker === 'binance' && 'Binance → Orders → Trade History → Export'}
              {broker === 'bybit' && 'Bybit → Orders → Order History → Export'}
              {broker === 'forex' && 'MT4/MT5 → Account History → Save as Detailed Report (CSV)'}
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:text-sm hover:file:bg-blue-500 cursor-pointer"
            />
          </div>

          {preview.length > 0 && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-800">
                <p className="text-white text-sm font-medium">Preview (first 5 rows)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-800">
                      <th className="text-left px-4 py-2">Symbol</th>
                      <th className="text-left px-4 py-2">Direction</th>
                      <th className="text-right px-4 py-2">Entry</th>
                      <th className="text-right px-4 py-2">Qty</th>
                      <th className="text-right px-4 py-2">P&L</th>
                      <th className="text-left px-4 py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((t, i) => (
                      <tr key={i} className="border-b border-gray-800">
                        <td className="px-4 py-2 text-white">{t.symbol}</td>
                        <td className={`px-4 py-2 capitalize ${t.direction === 'long' ? 'text-green-400' : 'text-red-400'}`}>{t.direction}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{t.entry_price}</td>
                        <td className="px-4 py-2 text-right text-gray-300">{t.quantity}</td>
                        <td className={`px-4 py-2 text-right ${t.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>{t.pnl}</td>
                        <td className="px-4 py-2 text-gray-400">{t.entry_date ? new Date(t.entry_date).toLocaleDateString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {file && (
            <button
              onClick={handleCSVImport}
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
            >
              {loading ? 'Importing...' : `Import from ${brokerLabel} CSV`}
            </button>
          )}
        </div>
      )}

      {/* API Sync Tab */}
      {tab === 'api' && (
        <div className="space-y-5">
          {broker === 'forex' ? (
            <div className="bg-gray-900 border border-yellow-700 rounded-xl p-5">
              <p className="text-yellow-400 text-sm">⚠️ FX brokers don't have a standard API. Please use CSV import for your FX broker.</p>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 space-y-4">
              <div>
                <p className="text-white font-medium mb-1">{brokerLabel} API Keys</p>
                <p className="text-gray-500 text-xs mb-4">
                  Use <span className="text-yellow-400">read-only</span> API keys. Never enable trading or withdrawal permissions.
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">API Key</label>
                <input value={apiKey} onChange={e => setApiKey(e.target.value)} className={inputClass} placeholder="Paste your API key..." />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">API Secret</label>
                <input type="password" value={apiSecret} onChange={e => setApiSecret(e.target.value)} className={inputClass} placeholder="Paste your API secret..." />
              </div>
              <button
                onClick={handleAPISync}
                disabled={loading || !apiKey || !apiSecret}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
              >
                {loading ? 'Syncing...' : `Sync from ${brokerLabel}`}
              </button>
            </div>
          )}
        </div>
      )}

      {status && (
        <div className={`mt-4 p-4 rounded-xl text-sm ${
          status.type === 'success' ? 'bg-green-900 border border-green-700 text-green-300' : 'bg-red-900 border border-red-700 text-red-300'
        }`}>
          {status.message}
        </div>
      )}
    </div>
  )
}